/**
 * บอสโลก (docs/systems/WORLD_BOSSES.md): three old powers of the wilds that come in turn, one an hour. Data only; the rules
 * (schedule, scaling, contribution, tiers, leash, enrage) are systems/worldBosses.ts and the move shapes
 * systems/bossMoves.ts. Their monsters and moves are folded into monsters.ts and skills.ts; a move's rule extends the
 * dungeon's `DungeonMoveRule` (scripted cone / ring / line, `breakCancels`, `guardStopsStun`, `armour`, `answer`).
 *
 * Distances are metres of the map. Every move has a wind-up of at least 0.7 s (the combat triangle must stay readable).
 */
import type { DungeonMoveRule } from './dungeon';
import type { MonsterDef } from './monsters';
import type { SkillDef, SkillFx, SkillKind } from './skills';
import type { ZoneId } from './zones';
import { WB_ADD_MONSTERS } from './worldBossMech';


export type WorldBossId = 'wb-ape' | 'wb-firebird' | 'wb-thunder-turtle';

/** Home map of each boss (H17): it spawns only there, at a random walkable spot outside the safe plaza. */
export const WB_HOME: Readonly<Record<WorldBossId, ZoneId>> = {
  'wb-ape': 'city-snow',
  'wb-firebird': 'city-desert',
  'wb-thunder-turtle': 'city-sea',
};
/** The random arena is at most this wide (tiles); the city maps are 96 across. */
export const WB_SPOT_R = 14;

/** Each boss comes every 6 hours of game time (21600 s), the three staggered 2 hours apart (4 a day each); `world.time` seconds. */
export const WB_CYCLE = {
  length: 21600,
  slot: 7200,
  /** The first boss of a fresh world comes 15 minutes in. */
  offset: 900,
  /** Seconds before it appears when the `soon` notice is sent. */
  warn: [600, 120] as readonly number[],
  /** Seconds a boss stays when nobody kills it. */
  life: 900,
} as const;

export const WB_ORDER: readonly WorldBossId[] = ['wb-ape', 'wb-firebird', 'wb-thunder-turtle'];

/** Attendance and health: hp = perPlayer × max(minN, n), n capped; it only grows within a fight. */
export const WB_SCALE = {
  perPlayer: 36000,
  minN: 5,
  maxN: 60,
  /** Seconds between attendance samples, and how long an action keeps a player counted. */
  sample: 10,
  active: 20,
  /** A simulated helper counts this much. */
  helperWeight: 0.5,
} as const;

/**
 * Very strong (H17): it needs a party of 4+. `dps` is a player's steady damage a second and `uptime` the share of the
 * life timer spent hitting; 3 players cannot empty the minimum health in `WB_CYCLE.life`, 4 can. `atkMul` hits harder.
 */
export const WB_PARTY = { min: 4, dps: 60, uptime: 0.9, atkMul: 1.5 } as const;

/** Phases change at these shares of พลังชีวิต; the staged beat lasts `beat` seconds (no damage, no moves). */
export const WB_PHASES = { at: [0.7, 0.35] as readonly number[], beat: 2.5 } as const;

/** ความคลั่ง: from the first hit, `at` seconds in. Wind-ups never go under `minWindup`. */
export const WB_ENRAGE = { at: 480, dmgMul: 1.5, windupMul: 0.8, minWindup: 0.5, bigEvery: 20 } as const;

/** The leash: nobody in the arena for `idle` seconds sends the boss home, healing `regen` of its health a second. */
export const WB_LEASH = { idle: 15, regen: 0.02, homeSpeedMul: 1.4 } as const;

export const WB_CONTRIB = {
  /** Points per 1,000 × damage / maxHp. Guarding a hit and healing an attacker pay `half` of it. */
  damage: 1000,
  half: 0.5,
  /** Per mechanic answered (a move's `credit`). */
  mechanic: 30,
  /** Presence: a point a second alive in the arena, capped. */
  presencePerSec: 1,
  presenceCap: 200,
  /** Points needed to be paid. */
  min: 40,
} as const;

/** Tiers by rank among the eligible: the top `gold` share, the next `silver` share, the rest copper. */
export const WB_TIERS = { gold: 0.1, silver: 0.3 } as const;
export type WbTier = 'gold' | 'silver' | 'copper';

/** The chest each tier pays (every boss alike): ตำลึง, แต้มฝึกปรือ, material count, the rare-roll multiplier. */
export const WB_CHEST: Readonly<Record<WbTier, { gold: number; cultivation: number; materials: number; rareMul: number }>> = {
  gold: { gold: 2000, cultivation: 600, materials: 4, rareMul: 2 },
  silver: { gold: 1200, cultivation: 400, materials: 3, rareMul: 1.3 },
  copper: { gold: 600, cultivation: 250, materials: 2, rareMul: 1 },
};
/** Full chests per boss per arena day; after that a kill pays this share of แต้มฝึกปรือ and this many ตำลึง only. */
export const WB_DAILY = { fullChests: 2, cultivationShare: 0.2, gold: 50 } as const;

/** The offline fill (mode `npc`): simulated players who walk in, fight at a fixed rate and take no loot. */
export const WB_SIM = {
  start: 6,
  perPhase: 2,
  /** Seconds after the boss lands before they reach it. */
  walkIn: 20,
  /** Damage a second each, and the share of health they never take it under (the player finishes it). */
  dps: 12,
  floor: 0.03,
  /** Chance one answers a mechanic move that pays `credit`. */
  mechanic: 0.65,
  names: ['เซียวหลิน', 'ฉางเฟิง', 'หลี่อวิ๋น', 'ม่อไป๋', 'ซูหนิง', 'จางอวี่', 'เฉินซาน', 'หวังเหยียน', 'ลั่วชิง', 'ถังหาน'] as readonly string[],
} as const;

// ───────────────────────── moves ─────────────────────────

/** How a world-boss move lands: the dungeon's rule plus where it centres, what it adds, and what it credits. */
export interface BossMoveRule extends DungeonMoveRule {
  /** Where a ring lands: on the caster (default), on the target, or on the farthest player (a leap). */
  readonly at?: 'target' | 'far';
  /** Several ground circles of radius `r` (the first on the target, the rest drawn from the arena). */
  readonly spots?: number;
  /** Two or more parallel lines `gap` metres apart (a swoop). */
  readonly parallel?: { readonly count: number; readonly gap: number };
  /** Seconds of มึนงง on a landed hit (guard stops it when `guardStopsStun`). */
  readonly stun?: number;
  /** A status put on everyone it lands on. */
  readonly status?: { readonly id: string; readonly stacks?: number };
  /** Only players off the ground (a ring of flame in the air). */
  readonly airOnly?: boolean;
  /** The caster's own buff (a chest beat): damage multiplier for some seconds. */
  readonly buff?: { readonly atkMul: number; readonly seconds: number };
  /** The mechanic this move pays +30 to whoever answers it (breaking the wind-up, ...). */
  readonly credit?: string;
}

export interface BossMove {
  readonly id: string;
  readonly name: string;
  readonly glyph: string;
  readonly kind: SkillKind;
  /** Phases it is used in. */
  readonly phases: readonly (1 | 2 | 3)[];
  readonly windup: number;
  readonly cooldown: number;
  /** Reach: it is used with the target between `min` and `max` metres. */
  readonly min: number;
  readonly max: number;
  /** Share of a geared 320-HP, 25-defence player's health a full hit takes (0.25 to 0.40). */
  readonly share: number;
  readonly weight: number;
  readonly fx: SkillFx;
  /** The phase-3 big move used every 20 s once enraged. */
  readonly big?: boolean;
  /** No ground mark: a plain single-target blow the World resolves (the pose tells it). */
  readonly plain?: boolean;
  /** Rule row for scripted moves. */
  readonly rule?: BossMoveRule;
  /** What answers it (docs, tests, art). */
  readonly answer: string;
}

const m = (o: BossMove): BossMove => o;

export interface WorldBossDef {
  readonly id: WorldBossId;
  readonly name: string;
  readonly glyph: string;
  readonly level: number;
  readonly atk: number;
  readonly def: number;
  readonly speed: number;
  readonly sprite: string;
  readonly scale: number;
  /** The arena: leash, attendance count and (pvp) the fight zone. One data row moves it to the 3 km region. */
  readonly arena: { readonly x: number; readonly y: number; readonly r: number };
  /** Where the boss first stands (the nest, the middle of the bay): the arena's centre unless set. */
  readonly moves: readonly BossMove[];
  /** Phase beats' text (shown as a notice). */
  readonly beats: readonly [string, string];
  readonly intro: string;
  /** Materials of the chest (ids in data/items.ts; the system pays those that exist) and the rare rolls. */
  readonly materials: readonly string[];
  readonly rare: readonly { readonly item: string; readonly chance: number }[];
}

export const WORLD_BOSSES: readonly WorldBossDef[] = [
  {
    id: 'wb-ape',
    name: 'ลิงยักษ์ทงปี้',
    glyph: '猿',
    level: 20,
    atk: 110,
    def: 30,
    speed: 3.2,
    sprite: 'wbApe',
    scale: 3,
    arena: { x: 240, y: 70, r: 60 },
    intro: 'ลิงยักษ์ทงปี้ลงจากผามาแล้ว!',
    beats: ['ทงปี้กระโจนขึ้นผา ขว้างหินใส่ทั้งลาน', 'ทงปี้ถอนต้นไม้ขึ้นมา ตาแดงก่ำ'],
    materials: ['wb-silver-fur', 'wb-ape-fang'],
    rare: [
      { item: 'pet-wb-ape-cub', chance: 0.03 },
      { item: 'wb-bell-sash', chance: 0.04 },
      { item: 'manual-wb-long-arm', chance: 0.02 },
      // Items v2 (data/bossGear.ts): the rest of ชุดวานรทงปี้ and two uniques.
      { item: 'wb-ape-band', chance: 0.05 },
      { item: 'wb-ape-greaves', chance: 0.05 },
      { item: 'wb-ape-boots', chance: 0.05 },
      { item: 'u-mountain-staff', chance: 0.01 },
      { item: 'u-cloud-boots', chance: 0.01 },
    ],
    moves: [
      m({
        id: 'wb-ape-sweep',
        name: 'กวาดแขนทะลวง',
        glyph: '掃',
        kind: 'attack',
        phases: [1, 2, 3],
        windup: 0.8,
        cooldown: 5,
        min: 0,
        max: 9,
        share: 0.3,
        weight: 3,
        fx: 'cleave',
        rule: { scripted: 'cone', r: 9, angle: 60, knockback: 4, answer: 'ตั้งรับ (守) หรือก้าวไปข้างหลังมัน' },
        answer: 'ตั้งรับ (守) หรือก้าวไปข้างหลังมัน',
      }),
      m({
        id: 'wb-ape-slam',
        name: 'ทุบพื้นสะเทือนป่า',
        glyph: '震',
        kind: 'break',
        phases: [1, 2, 3],
        windup: 1.2,
        cooldown: 9,
        min: 0,
        max: 8,
        share: 0.27,
        weight: 2,
        fx: 'quake',
        rule: {
          scripted: 'ring',
          r: 8,
          stun: 1,
          answer: 'กระโดดข้ามหรือแดชออกนอกวง (ตั้งรับไม่ช่วย)',
        },
        answer: 'กระโดดข้ามหรือแดชออกนอกวง (ตั้งรับไม่ช่วย)',
      }),
      m({
        id: 'wb-ape-boulder',
        name: 'ขว้างหินผา',
        glyph: '岩',
        kind: 'power',
        phases: [1, 2, 3],
        windup: 1.6,
        cooldown: 8,
        min: 4,
        max: 30,
        share: 0.3,
        weight: 2,
        fx: 'crush',
        rule: {
          scripted: 'ring',
          at: 'target',
          r: 5,
          stun: 0.8,
          answer: 'ก้าวออกนอกวงที่พื้น หลบหลังหินที่ตกลงมา',
        },
        answer: 'ก้าวออกนอกวงที่พื้น หลบหลังหินที่ตกลงมา',
      }),
      m({
        id: 'wb-ape-grab',
        name: 'คว้าบีบ',
        glyph: '捉',
        kind: 'break',
        phases: [1, 2],
        windup: 1.0,
        cooldown: 14,
        min: 0,
        max: 6,
        share: 0.27,
        weight: 1,
        fx: 'grab',
        rule: {
          scripted: 'line',
          len: 6,
          half: 1.2,
          status: { id: 'bleed', stacks: 2 },
          knockback: 10,
          credit: 'ape-grab',
          answer: 'หลบ หรือให้เพื่อนใช้ท่าทำลายใส่แขนมันเพื่อช่วย',
        },
        answer: 'หลบ หรือให้เพื่อนใช้ท่าทำลายใส่แขนมันเพื่อช่วย',
      }),
      m({
        id: 'wb-ape-ledge',
        name: 'ไต่ผาขว้างหิน',
        glyph: '崖',
        kind: 'power',
        phases: [2],
        windup: 1.6,
        cooldown: 6,
        min: 0,
        max: 90,
        share: 0.27,
        weight: 1,
        fx: 'crush',
        rule: {
          scripted: 'ring',
          at: 'target',
          spots: 3,
          r: 4,
          stun: 0.8,
          answer: 'ก้าวออกนอกวง หลบหลังหิน หรือไต่ผาขึ้นไปใช้ท่าทำลายสามครั้งใส่มัน',
        },
        answer: 'ก้าวออกนอกวง หลบหลังหิน หรือไต่ผาขึ้นไปใช้ท่าทำลายสามครั้งใส่มัน',
      }),
      m({
        id: 'wb-ape-mimic',
        name: 'เลียนกระบวนท่า',
        glyph: '仿',
        kind: 'buff',
        phases: [2, 3],
        windup: 3.0,
        cooldown: 25,
        min: 0,
        max: 40,
        share: 0,
        weight: 1,
        fx: 'aura',
        rule: {
          scripted: 'enrage',
          answer: 'ดูอักษรเหนือหัวมัน (攻 หรือ 破) แล้วเปลี่ยนไปใช้ท่าชนิดอื่นระหว่างที่มันตั้งท่า',
        },
        answer: 'ดูอักษรเหนือหัวมัน (攻 หรือ 破) แล้วเปลี่ยนไปใช้ท่าชนิดอื่นระหว่างที่มันตั้งท่า',
      }),
      m({
        id: 'wb-ape-tree',
        name: 'ถอนต้นไม้ฟาด',
        glyph: '木',
        kind: 'power',
        phases: [3],
        windup: 1.4,
        cooldown: 8,
        min: 0,
        max: 14,
        share: 0.35,
        weight: 3,
        fx: 'cleave',
        rule: { scripted: 'line', len: 14, half: 1.5, knockback: 6, answer: 'ก้าวหลบออกข้างแนว' },
        answer: 'ก้าวหลบออกข้างแนว',
      }),
      m({
        id: 'wb-ape-leap',
        name: 'กระโจนทับ',
        glyph: '跳',
        kind: 'power',
        phases: [3],
        windup: 1.5,
        cooldown: 14,
        min: 6,
        max: 40,
        share: 0.38,
        weight: 2,
        big: true,
        fx: 'quake',
        rule: {
          scripted: 'ring',
          at: 'far',
          r: 7,
          stun: 1.2,
          answer: 'วิ่งออกนอกวงที่พื้น การร่อนทำให้ลอยพ้นได้',
        },
        answer: 'วิ่งออกนอกวงที่พื้น การร่อนทำให้ลอยพ้นได้',
      }),
      m({
        id: 'wb-ape-chest',
        name: 'ตบอกคำราม',
        glyph: '吼',
        kind: 'buff',
        phases: [3],
        windup: 2.0,
        cooldown: 30,
        min: 0,
        max: 40,
        share: 0,
        weight: 1,
        fx: 'aura',
        rule: {
          scripted: 'enrage',
          buff: { atkMul: 1.3, seconds: 15 },
          breakCancels: true,
          credit: 'ape-chest',
          answer: 'ใช้ท่าทำลาย (破) ใส่ในสองวินาทีแรกเพื่อตัดท่า',
        },
        answer: 'ใช้ท่าทำลาย (破) ใส่ในสองวินาทีแรกเพื่อตัดท่า',
      }),
    ],
  },
  {
    id: 'wb-firebird',
    name: 'นกเพลิงปี้ฟาง',
    glyph: '鳥',
    level: 22,
    atk: 100,
    def: 22,
    speed: 2.4,
    sprite: 'wbFirebird',
    scale: 2.4,
    arena: { x: 555, y: 360, r: 45 },
    intro: 'นกเพลิงปี้ฟางบินลงมาจากยอดผาแดง!',
    beats: ['ปี้ฟางสยายปีกขึ้นฟ้า ฝนเพลิงกำลังจะตก', 'ปี้ฟางออกไข่เพลิงบนรัง ท้องฟ้าเริ่มแดง'],
    materials: ['wb-fire-feather', 'wb-eternal-ash'],
    rare: [
      { item: 'mount-wb-firebird', chance: 0.005 },
      { item: 'wb-firebird-cape', chance: 0.03 },
      { item: 'pet-wb-firebird-chick', chance: 0.03 },
      // Items v2 (data/bossGear.ts): the rest of ชุดขนนกเพลิง and two uniques.
      { item: 'wb-firebird-crown', chance: 0.05 },
      { item: 'wb-firebird-greaves', chance: 0.05 },
      { item: 'wb-firebird-boots', chance: 0.05 },
      { item: 'u-wind-fan', chance: 0.01 },
      { item: 'u-moon-sabre', chance: 0.01 },
    ],
    moves: [
      m({
        id: 'wb-bird-peck',
        name: 'จิกเพลิง',
        glyph: '啄',
        kind: 'attack',
        phases: [1, 2, 3],
        windup: 0.7,
        cooldown: 4,
        min: 0,
        max: 4,
        share: 0.28,
        weight: 3,
        plain: true,
        fx: 'pierce',
        answer: 'ตั้งรับ (守)',
      }),
      m({
        id: 'wb-bird-fan',
        name: 'ปีกพัดไฟ',
        glyph: '扇',
        kind: 'power',
        phases: [1, 2, 3],
        windup: 1.2,
        cooldown: 8,
        min: 0,
        max: 12,
        share: 0.3,
        weight: 2,
        fx: 'wind',
        rule: { scripted: 'cone', r: 12, angle: 45, answer: 'ก้าวออกนอกกรวยไฟ' },
        answer: 'ก้าวออกนอกกรวยไฟ',
      }),
      m({
        id: 'wb-bird-kick',
        name: 'เตะขาเดียว',
        glyph: '蹴',
        kind: 'break',
        phases: [1, 2, 3],
        windup: 1.0,
        cooldown: 9,
        min: 0,
        max: 5,
        share: 0.26,
        weight: 1,
        fx: 'crush',
        rule: { scripted: 'cone', r: 5, angle: 40, knockback: 3, answer: 'หลบด้วยแดช' },
        answer: 'หลบด้วยแดช',
      }),
      m({
        id: 'wb-bird-rain',
        name: 'ฝนเพลิง',
        glyph: '雨',
        kind: 'power',
        phases: [2, 3],
        windup: 2.0,
        cooldown: 16,
        min: 0,
        max: 45,
        share: 0.28,
        weight: 3,
        fx: 'ring',
        rule: {
          scripted: 'ring',
          at: 'target',
          spots: 12,
          r: 3,
          status: { id: 'poison', stacks: 1 },
          answer: 'ขยับตลอดเวลา หรือขึ้นอากาศ (ฝนตกเฉพาะพื้น)',
        },
        answer: 'ขยับตลอดเวลา หรือขึ้นอากาศ (ฝนตกเฉพาะพื้น)',
      }),
      m({
        id: 'wb-bird-dive',
        name: 'โฉบดิ่ง',
        glyph: '降',
        kind: 'power',
        phases: [2, 3],
        windup: 1.6,
        cooldown: 14,
        min: 6,
        max: 40,
        share: 0.35,
        weight: 2,
        fx: 'pierce',
        rule: { scripted: 'line', len: 20, half: 2, stun: 1, answer: 'ก้าวหลบออกข้างแนว แล้วรุมตีตอนมันลงพื้น' },
        answer: 'ก้าวหลบออกข้างแนว แล้วรุมตีตอนมันลงพื้น',
      }),
      m({
        id: 'wb-bird-sky-ring',
        name: 'วงไฟกลางอากาศ',
        glyph: '環',
        kind: 'power',
        phases: [2],
        windup: 1.2,
        cooldown: 12,
        min: 0,
        max: 10,
        share: 0.3,
        weight: 1,
        fx: 'ring',
        rule: { scripted: 'ring', r: 10, airOnly: true, answer: 'ร่อนออกนอกวง หรือลงพื้น' },
        answer: 'ร่อนออกนอกวง หรือลงพื้น',
      }),
      m({
        id: 'wb-bird-sky-burn',
        name: 'ปีกเพลิงพลิกฟ้า',
        glyph: '焚',
        kind: 'power',
        phases: [3],
        windup: 2.5,
        cooldown: 24,
        min: 0,
        max: 45,
        share: 0.38,
        weight: 2,
        big: true,
        fx: 'breath',
        rule: {
          scripted: 'ring',
          r: 40,
          status: { id: 'slow', stacks: 1 },
          answer: 'หลบใต้ชะง่อนผา หรือตั้งรับพร้อมพลังกายเต็ม (ลดครึ่งหนึ่ง)',
        },
        answer: 'หลบใต้ชะง่อนผา หรือตั้งรับพร้อมพลังกายเต็ม (ลดครึ่งหนึ่ง)',
      }),
      m({
        id: 'wb-bird-eggs',
        name: 'ออกไข่เพลิง',
        glyph: '卵',
        kind: 'buff',
        phases: [3],
        windup: 2.0,
        cooldown: 5,
        min: 0,
        max: 60,
        share: 0,
        weight: 1,
        fx: 'aura',
        rule: {
          scripted: 'enrage',
          breakCancels: true,
          credit: 'bird-eggs',
          answer: 'ใช้ท่าทำลาย (破) ตัดท่าตอนมันออกไข่ หรือทุบไข่ให้แตกก่อนฟักไฟหมด',
        },
        answer: 'ใช้ท่าทำลาย (破) ตัดท่าตอนมันออกไข่ หรือทุบไข่ให้แตกก่อนฟักไฟหมด',
      }),
      m({
        id: 'wb-bird-swoop',
        name: 'ร่อนเผาแนว',
        glyph: '掠',
        kind: 'power',
        phases: [3],
        windup: 1.0,
        cooldown: 12,
        min: 4,
        max: 40,
        share: 0.27,
        weight: 2,
        fx: 'wind',
        rule: {
          scripted: 'line',
          len: 40,
          half: 1.5,
          parallel: { count: 2, gap: 8 },
          answer: 'ยืนตรงกลางระหว่างสองแนว',
        },
        answer: 'ยืนตรงกลางระหว่างสองแนว',
      }),
    ],
  },
  {
    id: 'wb-thunder-turtle',
    name: 'เต่าคะนองเหล่ยกุย',
    glyph: '雷',
    level: 24,
    atk: 95,
    def: 40,
    speed: 1.6,
    sprite: 'wbTurtle',
    scale: 3.4,
    arena: { x: 430, y: 640, r: 70 },
    intro: 'เต่าคะนองเหล่ยกุยโผล่ขึ้นเหนือปากอ่าว!',
    beats: ['เหล่ยกุยหดหัวเข้ากระดอง ท้องฟ้าครึ้มลง', 'เหล่ยกุยตะกายขึ้นครึ่งตัว อักขระบนกระดองลุกโชน'],
    materials: ['wb-thunder-scale', 'wb-thunder-pearl'],
    rare: [
      { item: 'pet-wb-turtle-hatchling', chance: 0.04 },
      { item: 'wb-shell-pauldron', chance: 0.04 },
      { item: 'treasure-wb-calm-thunder-stone', chance: 0.02 },
      // Items v2 (data/bossGear.ts): the rest of ชุดกระดองอสนี and two uniques.
      { item: 'wb-shell-helm', chance: 0.05 },
      { item: 'wb-shell-greaves', chance: 0.05 },
      { item: 'wb-shell-boots', chance: 0.05 },
      { item: 'u-plum-needles', chance: 0.01 },
      { item: 'u-shadow-sword', chance: 0.01 },
    ],
    moves: [
      m({
        id: 'wb-turtle-bite',
        name: 'งับกระชาก',
        glyph: '咬',
        kind: 'attack',
        phases: [1, 2, 3],
        windup: 0.8,
        cooldown: 5,
        min: 0,
        max: 7,
        share: 0.3,
        weight: 3,
        fx: 'bite',
        rule: { scripted: 'cone', r: 7, angle: 25, knockback: -4, answer: 'ตั้งรับ (守)' },
        answer: 'ตั้งรับ (守)',
      }),
      m({
        id: 'wb-turtle-flipper',
        name: 'ฟาดครีบ',
        glyph: '鰭',
        kind: 'break',
        phases: [1, 2, 3],
        windup: 1.0,
        cooldown: 9,
        min: 0,
        max: 6,
        share: 0.26,
        weight: 2,
        fx: 'cleave',
        rule: { scripted: 'cone', r: 6, angle: 90, status: { id: 'slow', stacks: 1 }, answer: 'หลบด้วยแดช' },
        answer: 'หลบด้วยแดช',
      }),
      m({
        id: 'wb-turtle-rings',
        name: 'วงอัสนีบนผิวน้ำ',
        glyph: '雷',
        kind: 'power',
        phases: [1, 2, 3],
        windup: 1.4,
        cooldown: 14,
        min: 0,
        max: 24,
        share: 0.3,
        weight: 2,
        fx: 'ring',
        rule: { scripted: 'ring', r: 24, stun: 0.8, answer: 'ยืนบนหินหรือดาดฟ้า กระโดดข้ามวง' },
        answer: 'ยืนบนหินหรือดาดฟ้า กระโดดข้ามวง',
      }),
      m({
        id: 'wb-turtle-bolts',
        name: 'ฟ้าผ่าตามรอยกระดอง',
        glyph: '電',
        kind: 'power',
        phases: [2, 3],
        windup: 1.2,
        cooldown: 12,
        min: 0,
        max: 60,
        share: 0.28,
        weight: 3,
        fx: 'bolt',
        rule: { scripted: 'ring', at: 'target', spots: 5, r: 2.5, stun: 1, answer: 'ขยับตลอดเวลา เสาล่อฟ้าดึงสายฟ้าเข้าหาตัวมัน' },
        answer: 'ขยับตลอดเวลา เสาล่อฟ้าดึงสายฟ้าเข้าหาตัวมัน',
      }),
      m({
        id: 'wb-turtle-tide',
        name: 'คลื่นยักษ์ซัด',
        glyph: '潮',
        kind: 'power',
        phases: [2, 3],
        windup: 2.0,
        cooldown: 22,
        min: 0,
        max: 60,
        share: 0.3,
        weight: 1,
        fx: 'wind',
        rule: { scripted: 'line', len: 50, half: 25, knockback: 10, status: { id: 'slow', stacks: 2 }, answer: 'ปีนขึ้นหิน เรือ หรือท่าเรือ หรือวิ่งบนน้ำข้ามไป' },
        answer: 'ปีนขึ้นหิน เรือ หรือท่าเรือ หรือวิ่งบนน้ำข้ามไป',
      }),
      m({
        id: 'wb-turtle-ram',
        name: 'ดำดิ่งพุ่งชน',
        glyph: '衝',
        kind: 'power',
        phases: [3],
        windup: 1.5,
        cooldown: 14,
        min: 8,
        max: 60,
        share: 0.35,
        weight: 2,
        fx: 'crush',
        rule: { scripted: 'line', len: 30, half: 3, knockback: 6, answer: 'ก้าวออกจากรอยคลื่นที่ลากยาว' },
        answer: 'ก้าวออกจากรอยคลื่นที่ลากยาว',
      }),
      m({
        id: 'wb-turtle-thousand',
        name: 'กระดองอัสนีพันชั้น',
        glyph: '千',
        kind: 'power',
        phases: [3],
        windup: 3.0,
        cooldown: 28,
        min: 0,
        max: 70,
        share: 0.38,
        weight: 2,
        big: true,
        fx: 'quake',
        rule: {
          scripted: 'ring',
          r: 70,
          status: { id: 'silence', stacks: 1 },
          answer: 'ไปยืนบนหินที่มีเครื่องหมาย (มีหนึ่งก้อนที่วิ่งบนน้ำไปถึงได้เสมอ)',
        },
        answer: 'ไปยืนบนหินที่มีเครื่องหมาย (มีหนึ่งก้อนที่วิ่งบนน้ำไปถึงได้เสมอ)',
      }),
    ],
  },
];

export const WORLD_BOSS: Readonly<Record<string, WorldBossDef>> = Object.fromEntries(WORLD_BOSSES.map((b) => [b.id, b]));
export const isWorldBossId = (id: string): id is WorldBossId => id in WORLD_BOSS;

/** Move rows by skill id. */
export const WB_MOVE_RULES: Readonly<Record<string, BossMoveRule>> = Object.fromEntries(
  WORLD_BOSSES.flatMap((b) => b.moves.filter((x) => x.rule).map((x) => [x.id, x.rule!] as const)),
);
export const WB_MOVES: Readonly<Record<string, BossMove & { boss: WorldBossId }>> = Object.fromEntries(
  WORLD_BOSSES.flatMap((b) => b.moves.map((x) => [x.id, { ...x, boss: b.id }] as const)),
);

/** Power for a share of a geared player's health (320 HP, 25 defence: damage = atk × power × 100 / 125). */
export const movePower = (atk: number, share: number): number => Math.round(((share * 320 * 1.25) / atk) * 100) / 100;

/** The three monsters (folded into monsters.ts). Health is set at the fight from the attendance. */
export const WORLD_BOSS_MONSTERS: readonly MonsterDef[] = [...WORLD_BOSSES.map((b) => {
  const plain = b.moves.filter((x) => x.phases.includes(1) && x.kind === 'attack');
  const brk = b.moves.find((x) => x.phases.includes(1) && x.kind === 'break');
  return {
    id: b.id,
    name: b.name,
    level: b.level,
    hp: WB_SCALE.perPlayer * WB_SCALE.minN,
    atk: b.atk,
    def: b.def,
    speed: b.speed,
    aggro: b.arena.r,
    leash: b.arena.r,
    decisionRate: 1.2,
    attack: plain[0]!.id,
    ...(brk ? { break: brk.id } : {}),
    canBlock: false,
    reward: { cultivation: 0, gold: [0, 0] as const },
    respawn: null,
    sprite: b.sprite,
    scale: b.scale,
  };
}), ...WB_ADD_MONSTERS];

/** The moves as skills (folded into skills.ts): scripted ones wind up in the World and systems/worldBosses.ts resolves them. */
export const WORLD_BOSS_SKILLS: readonly SkillDef[] = WORLD_BOSSES.flatMap((b) =>
  b.moves.map(
    (x): SkillDef => ({
      id: x.id,
      name: x.name,
      glyph: x.glyph,
      kind: x.kind,
      qi: 0,
      cooldown: x.cooldown,
      windup: x.windup,
      range: x.max,
      power: movePower(b.atk, x.share),
      fx: x.fx,
      desc: x.answer,
      ...(x.rule ? { scripted: true, armour: true } : {}),
    }),
  ),
);

/** Names of the simulated players, cycled. */
export const simName = (i: number): string => WB_SIM.names[i % WB_SIM.names.length]!;

// ───────────────────────── the clock ─────────────────────────

/** The boss up at a time (`world.time`) and the seconds since it landed, or null. */
export function bossAt(time: number): { k: number; id: WorldBossId; t: number } | null {
  if (time < WB_CYCLE.offset) return null;
  const k = Math.floor((time - WB_CYCLE.offset) / WB_CYCLE.slot);
  const t = time - WB_CYCLE.offset - k * WB_CYCLE.slot;
  return t < WB_CYCLE.life ? { k, id: WB_ORDER[k % WB_ORDER.length]!, t } : null;
}

/** The slot's start time. */
export const slotStart = (k: number): number => WB_CYCLE.offset + k * WB_CYCLE.slot;

/** The next boss to land after `time` (not the one that is up), the seconds until, and its slot. */
export function nextBoss(time: number): { k: number; id: WorldBossId; in: number } {
  const k = time < WB_CYCLE.offset ? 0 : Math.floor((time - WB_CYCLE.offset) / WB_CYCLE.slot) + 1;
  return { k, id: WB_ORDER[k % WB_ORDER.length]!, in: slotStart(k) - time };
}

// ───────────────────────── scaling and tiers ─────────────────────────

/** Health for an attendance (people-equivalents, helpers weighed 0.5). */
export const bossHp = (n: number): number =>
  WB_SCALE.perPlayer * Math.max(WB_SCALE.minN, Math.min(WB_SCALE.maxN, Math.floor(n)));

/** Contribution points of one fighter (the parts are damage, guard and heal as shares of max health). */
export function contribution(p: {
  dmg: number;
  guard: number;
  heal: number;
  mech: number;
  present: number;
  maxHp: number;
}): number {
  const rate = WB_CONTRIB.damage / Math.max(1, p.maxHp);
  return (
    p.dmg * rate +
    (p.guard + p.heal) * rate * WB_CONTRIB.half +
    p.mech * WB_CONTRIB.mechanic +
    Math.min(WB_CONTRIB.presenceCap, p.present * WB_CONTRIB.presencePerSec)
  );
}

/** Tier by rank (0 first) among `count` eligible. */
export function tierOf(rank: number, count: number): WbTier {
  const gold = Math.max(1, Math.ceil(count * WB_TIERS.gold));
  const silver = Math.ceil(count * WB_TIERS.silver);
  return rank < gold ? 'gold' : rank < gold + silver ? 'silver' : 'copper';
}

// ───────────────────────── save ─────────────────────────

/** The arena day's full-chest claims per boss (progress.worldBoss); absent until the first kill. */
export interface WorldBossSave {
  day: string;
  claims: Record<string, number>;
  kills: number;
}
export const newWorldBossSave = (): WorldBossSave => ({ day: '', claims: {}, kills: 0 });
export function parseWorldBossSave(raw: unknown): WorldBossSave {
  const r = (raw && typeof raw === 'object' ? raw : {}) as Partial<WorldBossSave>;
  const n = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? Math.max(0, Math.floor(v)) : 0);
  const claims: Record<string, number> = {};
  if (r.claims && typeof r.claims === 'object')
    for (const [k, v] of Object.entries(r.claims)) if (isWorldBossId(k)) claims[k] = n(v);
  return { day: typeof r.day === 'string' ? r.day : '', claims, kills: n(r.kills) };
}


// ───────────────────────── rewards (open tagging) ─────────────────────────

/** The last hit's chest is the gold chest times this. */
export const WB_LAST_HIT_MUL = 1.5;

export interface WbShare {
  readonly id: number;
  readonly lastHit: boolean;
  readonly tier: WbTier;
  readonly gold: number;
  readonly cultivation: number;
  readonly materials: number;
  readonly rareMul: number;
}

/**
 * Everyone who took part (contribution at least `WB_CONTRIB.min`, or the last hitter) is paid their tier chest by
 * rank; the player who landed the killing blow gets the gold chest times `WB_LAST_HIT_MUL`, more than any other.
 * Anyone may join the fight; contribution decides who took part, not who owns it.
 */
export function splitRewards(
  parts: readonly { id: number; dmg: number; guard: number; heal: number; mech: number; present: number; auto?: boolean }[],
  maxHp: number,
  lastHitId: number,
): WbShare[] {
  const ranked = parts
    .map((p) => ({ id: p.id, auto: !!p.auto, pts: contribution({ ...p, maxHp }) }))
    .filter((x) => x.pts >= WB_CONTRIB.min || x.id === lastHitId)
    .sort((a, b) => b.pts - a.pts || b.id - a.id);
  return ranked.map((x, rank) => {
    const lastHit = x.id === lastHitId;
    // J3: an auto player is paid the helper (copper) tier only, with no last-hit chest.
    const tier = x.auto ? 'copper' : lastHit ? 'gold' : tierOf(rank, ranked.length);
    const c = WB_CHEST[tier];
    const m = lastHit && !x.auto ? WB_LAST_HIT_MUL : 1;
    return {
      id: x.id,
      lastHit,
      tier,
      gold: Math.round(c.gold * m),
      cultivation: Math.round(c.cultivation * m),
      materials: Math.round(c.materials * m),
      rareMul: c.rareMul * m,
    };
  });
}
